import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export type BulletinCategory = 'thong_bao' | 'huong_dan' | 'vi_pham' | 'khen_thuong' | 'khan_cap' | 'giao_viec';
export type BulletinPriority = 'normal' | 'important' | 'urgent';
export type BulletinStatus = 'draft' | 'published' | 'archived';
export type BulletinTargetType = 'all' | 'specific' | 'department';
export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export interface BulletinMediaItem {
  fileId: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
}

export interface Bulletin {
  id: string;
  category: BulletinCategory;
  priority: BulletinPriority;
  title: string;
  summary: string;
  content: string;
  media_files: BulletinMediaItem[];
  video_url: string | null;
  target_type: BulletinTargetType;
  target_department?: string | null;
  is_pinned: boolean;
  require_ack: boolean;
  status: BulletinStatus;
  author_id: string;
  author_name: string;
  created_at: string;
  updated_at: string;
  recipient_ids?: string[];
  recipient_names?: string[];
  // Task specific fields
  task_status?: TaskStatus;
  claimed_by_id?: string | null;
  claimed_by_name?: string | null;
  claimed_at?: string | null;
  due_date?: string | null;
  completed_at?: string | null;
  completion_note?: string | null;
  // Stats for Admin
  total_recipients?: number;
  read_count?: number;
  ack_count?: number;
  // Staff personal read status
  is_read?: boolean;
  read_at?: string | null;
  is_acknowledged?: boolean;
  acknowledged_at?: string | null;
}

export const DEPARTMENT_OPTIONS = [
  { value: 'SX_AO', label: 'Sản xuất áo (SX_AO)' },
  { value: 'CSKH', label: 'Chăm sóc khách hàng (CSKH)' },
  { value: 'SX_THE', label: 'Sản xuất thẻ (SX_THE)' },
];

export const TASK_STATUS_MAP: Record<TaskStatus, { label: string; icon: string; color: string; bg: string }> = {
  pending: { label: 'Chờ nhận việc', icon: 'fas fa-hourglass-start', color: '#d97706', bg: '#fef3c7' },
  in_progress: { label: 'Đang thực hiện', icon: 'fas fa-spinner fa-spin', color: '#2563eb', bg: '#dbeafe' },
  completed: { label: 'Đã hoàn thành', icon: 'fas fa-check-circle', color: '#16a34a', bg: '#dcfce7' },
  cancelled: { label: 'Đã hủy', icon: 'fas fa-ban', color: '#dc2626', bg: '#fee2e2' },
};

interface StaffMember {
  id: string;
  name: string;
  role?: string;
  status?: string;
}

interface RecipientTrack {
  staff_id: string;
  staff_name: string;
  role: string | null;
  is_read: boolean;
  read_at: string | null;
  is_acknowledged: boolean;
  acknowledged_at: string | null;
}

interface TrackingData {
  bulletin: Bulletin;
  recipients: RecipientTrack[];
  summary: {
    total: number;
    read: number;
    unread: number;
    acknowledged: number;
    percent_read: number;
  };
}

const CATEGORY_MAP: Record<BulletinCategory, { label: string; icon: string; color: string; bg: string }> = {
  thong_bao: { label: 'Thông báo', icon: 'fas fa-bullhorn', color: '#8d2a2c', bg: 'rgba(141, 42, 44, 0.09)' },
  huong_dan: { label: 'Hướng dẫn / SOP', icon: 'fas fa-book-open', color: '#1e7e48', bg: 'rgba(30, 126, 72, 0.1)' },
  vi_pham: { label: 'Vi phạm / Nhắc nhở', icon: 'fas fa-exclamation-triangle', color: '#c0392b', bg: 'rgba(192, 57, 43, 0.1)' },
  khen_thuong: { label: 'Khen thưởng / Vinh danh', icon: 'fas fa-award', color: '#b7791f', bg: 'rgba(183, 121, 31, 0.12)' },
  khan_cap: { label: 'Khẩn cấp', icon: 'fas fa-bolt', color: '#781d20', bg: 'rgba(120, 29, 32, 0.15)' },
  giao_viec: { label: 'Giao việc / Nhiệm vụ', icon: 'fas fa-tasks', color: '#2980b9', bg: 'rgba(41, 128, 185, 0.12)' },
};

const PRIORITY_MAP: Record<BulletinPriority, { label: string; color: string }> = {
  normal: { label: 'Bình thường', color: '#726969' },
  important: { label: 'Quan trọng', color: '#b7791f' },
  urgent: { label: 'Khẩn cấp', color: '#8d2a2c' },
};

export const AdminBulletinPage: React.FC = () => {
  const { user } = useAuth();
  const [bulletins, setBulletins] = useState<Bulletin[]>([]);
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [taskStatusFilter, setTaskStatusFilter] = useState<string>('all');
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  // Modal Create/Edit state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBulletin, setEditingBulletin] = useState<Bulletin | null>(null);
  const [formCategory, setFormCategory] = useState<BulletinCategory>('thong_bao');
  const [formPriority, setFormPriority] = useState<BulletinPriority>('normal');
  const [formTitle, setFormTitle] = useState('');
  const [formSummary, setFormSummary] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formVideoUrl, setFormVideoUrl] = useState('');
  const [formTargetType, setFormTargetType] = useState<BulletinTargetType>('all');
  const [formTargetDepartment, setFormTargetDepartment] = useState<string>('SX_AO');
  const [formRecipientIds, setFormRecipientIds] = useState<string[]>([]);
  const [formDueDate, setFormDueDate] = useState<string>('');
  const [formClaimedById, setFormClaimedById] = useState<string>('');
  const [formTaskStatus, setFormTaskStatus] = useState<TaskStatus>('pending');
  const [formIsPinned, setFormIsPinned] = useState(false);
  const [formRequireAck, setFormRequireAck] = useState(false);
  const [formStatus, setFormStatus] = useState<BulletinStatus>('published');
  const [existingMediaFiles, setExistingMediaFiles] = useState<BulletinMediaItem[]>([]);
  const [selectedUploadFiles, setSelectedUploadFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Modal Tracking state
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [isTrackingLoading, setIsTrackingLoading] = useState(false);
  const [trackingSearch, setTrackingSearch] = useState('');
  const [trackingFilter, setTrackingFilter] = useState<'all' | 'read' | 'unread'>('all');

  // Preview Modal state
  const [previewBulletin, setPreviewBulletin] = useState<Bulletin | null>(null);

  const authHeaders = useMemo(() => {
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('auth_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }, [user?.id]);

  // Load Staff List
  const loadStaffList = useCallback(async () => {
    try {
      const res = await fetch('/api/staff?all=true', { headers: authHeaders });
      const data = await res.json();
      if (data.success) {
        const active = (data.data || []).filter((s: StaffMember) => s.status !== 'Đã nghỉ');
        setStaffList(active);
      }
    } catch (err) {
      console.error('Error fetching staff list:', err);
    }
  }, [authHeaders]);

  // Load Bulletins
  const loadBulletins = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params = new URLSearchParams({ credentials: 'true', asAdmin: 'true' });
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (departmentFilter !== 'all') params.append('department', departmentFilter);
      if (taskStatusFilter !== 'all') params.append('task_status', taskStatusFilter);
      if (searchKeyword.trim()) params.append('search', searchKeyword.trim());

      const res = await fetch(`/api/bulletins?${params.toString()}`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Không thể tải dữ liệu bảng tin');
      }
      setBulletins(data.data || []);
    } catch (err: any) {
      setError(err.message || 'Lỗi khi tải dữ liệu');
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders, categoryFilter, priorityFilter, statusFilter, departmentFilter, taskStatusFilter, searchKeyword]);

  useEffect(() => {
    loadStaffList();
  }, [loadStaffList]);

  useEffect(() => {
    loadBulletins();
  }, [loadBulletins]);

  // Open Create Modal
  const handleOpenCreate = () => {
    setEditingBulletin(null);
    setFormCategory('thong_bao');
    setFormPriority('normal');
    setFormTitle('');
    setFormSummary('');
    setFormContent('');
    setFormVideoUrl('');
    setFormTargetType('all');
    setFormTargetDepartment('SX_AO');
    setFormRecipientIds([]);
    setFormDueDate('');
    setFormClaimedById('');
    setFormTaskStatus('pending');
    setFormIsPinned(false);
    setFormRequireAck(false);
    setFormStatus('published');
    setExistingMediaFiles([]);
    setSelectedUploadFiles([]);
    setStaffSearchQuery('');
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (bulletin: Bulletin) => {
    setEditingBulletin(bulletin);
    setFormCategory(bulletin.category);
    setFormPriority(bulletin.priority);
    setFormTitle(bulletin.title);
    setFormSummary(bulletin.summary || '');
    setFormContent(bulletin.content);
    setFormVideoUrl(bulletin.video_url || '');
    setFormTargetType(bulletin.target_type);
    setFormTargetDepartment(bulletin.target_department || 'SX_AO');
    setFormRecipientIds(bulletin.recipient_ids || []);
    setFormDueDate(bulletin.due_date ? bulletin.due_date.slice(0, 16) : '');
    setFormClaimedById(bulletin.claimed_by_id || '');
    setFormTaskStatus(bulletin.task_status || 'pending');
    setFormIsPinned(bulletin.is_pinned);
    setFormRequireAck(bulletin.require_ack);
    setFormStatus(bulletin.status);
    setExistingMediaFiles(bulletin.media_files || []);
    setSelectedUploadFiles([]);
    setStaffSearchQuery('');
    setIsModalOpen(true);
  };

  // Open Tracking Modal
  const handleOpenTracking = async (bulletinId: string) => {
    setIsTrackingModalOpen(true);
    setIsTrackingLoading(true);
    setTrackingSearch('');
    setTrackingFilter('all');
    try {
      const res = await fetch(`/api/bulletins/${bulletinId}/tracking`, { headers: authHeaders });
      const data = await res.json();
      if (data.success) {
        setTrackingData(data.data);
      } else {
        alert(data.error || 'Không thể tải dữ liệu theo dõi');
      }
    } catch (err) {
      console.error('Error fetching tracking:', err);
      alert('Lỗi kết nối máy chủ');
    } finally {
      setIsTrackingLoading(false);
    }
  };

  // Handle Delete Bulletin
  const handleDeleteBulletin = async (id: string, title: string) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa tin: "${title}"?\nHành động này không thể hoàn tác.`)) {
      return;
    }
    try {
      const res = await fetch(`/api/bulletins/${id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      const data = await res.json();
      if (data.success) {
        setBulletins((prev) => prev.filter((b) => b.id !== id));
      } else {
        alert(data.error || 'Xóa tin thất bại');
      }
    } catch (err) {
      alert('Lỗi khi xóa tin tức');
    }
  };

  // Handle File Selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setSelectedUploadFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleRemoveSelectedFile = (index: number) => {
    setSelectedUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingMedia = (fileId: string) => {
    setExistingMediaFiles((prev) => prev.filter((m) => m.fileId !== fileId));
  };

  // Handle Staff Checkbox Toggle
  const handleToggleStaffRecipient = (staffId: string) => {
    setFormRecipientIds((prev) =>
      prev.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId]
    );
  };

  const handleSelectAllStaff = () => {
    setFormRecipientIds(filteredStaffList.map((s) => s.id));
  };

  const handleDeselectAllStaff = () => {
    setFormRecipientIds([]);
  };

  // Filtered staff list for search inside modal
  const filteredStaffList = useMemo(() => {
    if (!staffSearchQuery.trim()) return staffList;
    const q = staffSearchQuery.toLowerCase();
    return staffList.filter(
      (s) => s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q) || (s.role && s.role.toLowerCase().includes(q))
    );
  }, [staffList, staffSearchQuery]);

  // Form Submit (Create / Edit)
  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) {
      alert('Vui lòng nhập tiêu đề thông tin');
      return;
    }
    if (!formContent.trim()) {
      alert('Vui lòng nhập nội dung chi tiết');
      return;
    }
    if (formTargetType === 'specific' && formRecipientIds.length === 0) {
      alert('Bạn đã chọn phân bổ cho từng nhân viên. Vui lòng chọn ít nhất 1 nhân viên nhận tin.');
      return;
    }

    try {
      setIsSubmitting(true);

      // Step 1: Upload new media files if any
      let uploadedMedia: BulletinMediaItem[] = [];
      if (selectedUploadFiles.length > 0) {
        const formData = new FormData();
        selectedUploadFiles.forEach((file) => {
          formData.append('files', file);
        });
        formData.append('description', `Bulletin: ${formTitle.slice(0, 100)}`);

        const uploadRes = await fetch('/api/bulletins/upload-media', {
          method: 'POST',
          headers: authHeaders,
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (!uploadRes.ok || !uploadData.success) {
          throw new Error(uploadData.error || 'Lỗi khi tải lên file ảnh/video');
        }
        uploadedMedia = uploadData.data || [];
      }

      // Combine existing media files and newly uploaded
      const finalMediaFiles = [...existingMediaFiles, ...uploadedMedia];
      const claimedStaff = formClaimedById ? staffList.find((s) => s.id === formClaimedById) : null;

      const payload = {
        category: formCategory,
        priority: formPriority,
        title: formTitle.trim(),
        summary: formSummary.trim() || undefined,
        content: formContent,
        media_files: finalMediaFiles,
        video_url: formVideoUrl.trim() || undefined,
        target_type: formTargetType,
        target_department: formTargetType === 'department' ? formTargetDepartment : undefined,
        recipient_ids: formTargetType === 'specific' ? formRecipientIds : [],
        is_pinned: formIsPinned,
        require_ack: formRequireAck,
        status: formStatus,
        task_status: formCategory === 'giao_viec' ? formTaskStatus : undefined,
        claimed_by_id: formCategory === 'giao_viec' ? (formClaimedById || null) : undefined,
        claimed_by_name: formCategory === 'giao_viec' ? (claimedStaff ? claimedStaff.name : null) : undefined,
        due_date: formCategory === 'giao_viec' ? (formDueDate || null) : undefined,
      };

      if (editingBulletin) {
        // Update
        const res = await fetch(`/api/bulletins/${editingBulletin.id}`, {
          method: 'PUT',
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || 'Không thể cập nhật bài viết');
        }
      } else {
        // Create
        const res = await fetch('/api/bulletins', {
          method: 'POST',
          headers: {
            ...authHeaders,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || 'Không thể tạo bài viết');
        }
      }

      setIsModalOpen(false);
      loadBulletins();
    } catch (err: any) {
      alert(err.message || 'Đã có lỗi xảy ra');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Stats calculation
  const stats = useMemo(() => {
    const total = bulletins.length;
    const thongBaoCount = bulletins.filter((b) => b.category === 'thong_bao').length;
    const huongDanCount = bulletins.filter((b) => b.category === 'huong_dan').length;
    const viPhamCount = bulletins.filter((b) => b.category === 'vi_pham').length;
    const khenThuongCount = bulletins.filter((b) => b.category === 'khen_thuong').length;
    const giaoViecCount = bulletins.filter((b) => b.category === 'giao_viec').length;
    const urgentCount = bulletins.filter((b) => b.priority === 'urgent').length;
    return { total, thongBaoCount, huongDanCount, viPhamCount, khenThuongCount, giaoViecCount, urgentCount };
  }, [bulletins]);

  // Tracking filtered recipients
  const filteredTrackingRecipients = useMemo(() => {
    if (!trackingData) return [];
    let list = trackingData.recipients;
    if (trackingFilter === 'read') list = list.filter((r) => r.is_read);
    if (trackingFilter === 'unread') list = list.filter((r) => !r.is_read);
    if (trackingSearch.trim()) {
      const q = trackingSearch.toLowerCase();
      list = list.filter((r) => r.staff_name.toLowerCase().includes(q) || r.staff_id.toLowerCase().includes(q));
    }
    return list;
  }, [trackingData, trackingFilter, trackingSearch]);

  return (
    <div className="bulletin-admin-container">
      {/* Top Header */}
      <div className="bulletin-admin-header">
        <div>
          <h1 className="bulletin-admin-title">Bảng tin & Nhiệm vụ nội bộ</h1>
          <p className="bulletin-admin-subtitle">
            Quản lý thông báo, quy trình hướng dẫn và phân công nhiệm vụ cho các bộ phận
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Link
            to="/admin-discipline-rewards"
            className="btn-secondary-action"
            style={{
              textDecoration: 'none',
              padding: '9px 16px',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              fontSize: '13.5px',
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              background: '#ffffff',
              border: '1px solid var(--border)',
              color: 'var(--text-primary)',
              boxShadow: 'var(--shadow-sm)',
              cursor: 'pointer',
              transition: 'var(--transition)',
            }}
          >
            Thống kê lỗi / thưởng
          </Link>
          <button className="bulletin-btn-create" onClick={handleOpenCreate}>
            <i className="fas fa-plus" style={{ fontSize: '13px' }}></i>
            Tạo thông tin mới
          </button>
        </div>
      </div>

      {/* Overview Metric Cards */}
      <div className="bulletin-metric-grid">
        <div className="bulletin-metric-card">
          <div className="metric-icon" style={{ background: 'rgba(141, 42, 44, 0.08)', color: '#8d2a2c' }}>
            <i className="fas fa-newspaper"></i>
          </div>
          <div className="metric-info">
            <span className="metric-value">{stats.total}</span>
            <span className="metric-label">Tổng số bài tin</span>
          </div>
        </div>

        <div className="bulletin-metric-card">
          <div className="metric-icon" style={{ background: 'rgba(30, 126, 72, 0.08)', color: '#1e7e48' }}>
            <i className="fas fa-book"></i>
          </div>
          <div className="metric-info">
            <span className="metric-value">{stats.huongDanCount}</span>
            <span className="metric-label">Hướng dẫn / SOP</span>
          </div>
        </div>

        <Link
          to="/admin-discipline-rewards"
          className="bulletin-metric-card"
          style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
          title="Bấm để xem thống kê chi tiết vi phạm"
        >
          <div className="metric-icon" style={{ background: 'rgba(192, 57, 43, 0.08)', color: '#c0392b' }}>
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="metric-info">
            <span className="metric-value">{stats.viPhamCount}</span>
            <span className="metric-label">Vi phạm / Nhắc nhở ↗</span>
          </div>
        </Link>

        <Link
          to="/admin-discipline-rewards"
          className="bulletin-metric-card"
          style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
          title="Bấm để xem thống kê chi tiết khen thưởng"
        >
          <div className="metric-icon" style={{ background: 'rgba(183, 121, 31, 0.08)', color: '#b7791f' }}>
            <i className="fas fa-award"></i>
          </div>
          <div className="metric-info">
            <span className="metric-value">{stats.khenThuongCount}</span>
            <span className="metric-label">Khen thưởng ↗</span>
          </div>
        </Link>

        <div
          className="bulletin-metric-card"
          onClick={() => setCategoryFilter(categoryFilter === 'giao_viec' ? 'all' : 'giao_viec')}
          style={{ cursor: 'pointer' }}
          title="Bấm để lọc danh sách nhiệm vụ / task"
        >
          <div className="metric-icon" style={{ background: 'rgba(41, 128, 185, 0.1)', color: '#2980b9' }}>
            <i className="fas fa-tasks"></i>
          </div>
          <div className="metric-info">
            <span className="metric-value">{stats.giaoViecCount}</span>
            <span className="metric-label">Nhiệm vụ / Task</span>
          </div>
        </div>

        <div className="bulletin-metric-card">
          <div className="metric-icon" style={{ background: 'rgba(120, 29, 32, 0.12)', color: '#781d20' }}>
            <i className="fas fa-bolt"></i>
          </div>
          <div className="metric-info">
            <span className="metric-value">{stats.urgentCount}</span>
            <span className="metric-label">Tin khẩn cấp</span>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bulletin-filter-bar">
        <div className="filter-group">
          <label>Loại thông tin:</label>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="all">Tất cả loại tin</option>
            <option value="thong_bao">Thông báo</option>
            <option value="huong_dan">Hướng dẫn / SOP</option>
            <option value="giao_viec">Giao việc / Task</option>
            <option value="vi_pham">Vi phạm / Nhắc nhở</option>
            <option value="khen_thuong">Khen thưởng</option>
            <option value="khan_cap">Khẩn cấp</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Bộ phận:</label>
          <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
            <option value="all">Tất cả bộ phận</option>
            {DEPARTMENT_OPTIONS.map((dep) => (
              <option key={dep.value} value={dep.value}>
                {dep.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Tiến độ task:</label>
          <select value={taskStatusFilter} onChange={(e) => setTaskStatusFilter(e.target.value)}>
            <option value="all">Tất cả tiến độ</option>
            <option value="pending">Chờ nhận việc</option>
            <option value="in_progress">Đang thực hiện</option>
            <option value="completed">Đã hoàn thành</option>
            <option value="cancelled">Đã hủy</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Mức độ:</label>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <option value="all">Tất cả mức độ</option>
            <option value="normal">Bình thường</option>
            <option value="important">Quan trọng</option>
            <option value="urgent">Khẩn cấp</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Trạng thái:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Tất cả trạng thái</option>
            <option value="published">Đã phát hành</option>
            <option value="draft">Bản nháp</option>
            <option value="archived">Lưu trữ</option>
          </select>
        </div>

        <div className="search-group" style={{ flex: 1 }}>
          <div className="search-input-wrapper">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              placeholder="Tìm kiếm tiêu đề, nội dung..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
            />
            {searchKeyword && (
              <button className="clear-search-btn" onClick={() => setSearchKeyword('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error or Loading */}
      {isLoading && (
        <div className="bulletin-state-box">
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '28px', color: '#8d2a2c' }}></i>
          <p>Đang tải danh sách bảng tin...</p>
        </div>
      )}

      {error && (
        <div className="bulletin-error-banner">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={loadBulletins}>Thử lại</button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && bulletins.length === 0 && (
        <div className="bulletin-state-box">
          <i className="fas fa-inbox" style={{ fontSize: '48px', color: '#c4b5b2', marginBottom: '12px' }}></i>
          <h3>Chưa có thông tin nào phù hợp</h3>
          <p>Hãy tạo bản tin mới để thông báo hoặc chia sẻ hướng dẫn cho nhân viên.</p>
          <button className="bulletin-btn-create" style={{ marginTop: '14px' }} onClick={handleOpenCreate}>
            <i className="fas fa-plus-circle" style={{ marginRight: '8px' }}></i>
            Tạo thông tin đầu tiên
          </button>
        </div>
      )}

      {/* Bulletin Cards List */}
      {!isLoading && bulletins.length > 0 && (
        <div className="bulletin-admin-list">
          {bulletins.map((item) => {
            const catInfo = CATEGORY_MAP[item.category] || CATEGORY_MAP.thong_bao;
            const priorityInfo = PRIORITY_MAP[item.priority] || PRIORITY_MAP.normal;
            const totalRecipients = item.total_recipients || 0;
            const readCount = item.read_count || 0;
            const percentRead = totalRecipients > 0 ? Math.round((readCount / totalRecipients) * 100) : 0;
            const imageCount = (item.media_files || []).filter((m) => m.mimeType.startsWith('image/')).length;
            const videoCount = (item.media_files || []).filter((m) => m.mimeType.startsWith('video/')).length;

            return (
              <div key={item.id} className={`bulletin-admin-card ${item.is_pinned ? 'is-pinned' : ''}`}>
                <div className="card-top-row">
                  <div className="card-badges">
                    <span className="category-badge" style={{ color: catInfo.color, background: catInfo.bg }}>
                      <i className={catInfo.icon} style={{ marginRight: '6px' }}></i>
                      {catInfo.label}
                    </span>

                    {item.priority !== 'normal' && (
                      <span className="priority-badge" style={{ color: priorityInfo.color, borderColor: priorityInfo.color }}>
                        {item.priority === 'urgent' && <i className="fas fa-bolt" style={{ marginRight: '4px' }}></i>}
                        {priorityInfo.label}
                      </span>
                    )}

                    {item.category === 'giao_viec' && item.task_status && (
                      <span className={`task-status-badge status-${item.task_status}`}>
                        <i className={TASK_STATUS_MAP[item.task_status]?.icon || 'fas fa-tasks'}></i>{' '}
                        {TASK_STATUS_MAP[item.task_status]?.label}
                        {item.claimed_by_name ? ` (${item.claimed_by_name})` : ''}
                      </span>
                    )}

                    {item.is_pinned && (
                      <span className="pinned-badge" title="Được ghim lên đầu">
                        <i className="fas fa-thumbtack"></i> Ghim
                      </span>
                    )}

                    <span className={`status-badge status-${item.status}`}>
                      {item.status === 'published' ? 'Đã phát hành' : item.status === 'draft' ? 'Bản nháp' : 'Lưu trữ'}
                    </span>
                  </div>

                  <div className="card-actions">
                    <button
                      className="btn-action-icon"
                      title="Xem trước"
                      onClick={() => setPreviewBulletin(item)}
                    >
                      <i className="fas fa-eye"></i>
                    </button>
                    <button
                      className="btn-action-icon"
                      title="Xem ai đã đọc / chưa đọc"
                      onClick={() => handleOpenTracking(item.id)}
                    >
                      <i className="fas fa-chart-pie"></i>
                    </button>
                    <button
                      className="btn-action-icon"
                      title="Chỉnh sửa"
                      onClick={() => handleOpenEdit(item)}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn-action-icon btn-delete"
                      title="Xóa bài viết"
                      onClick={() => handleDeleteBulletin(item.id, item.title)}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>

                <h3 className="card-title">{item.title}</h3>

                {item.summary && <p className="card-summary">{item.summary}</p>}

                {item.completion_note && (
                  <div className="card-completion-note" style={{ margin: '8px 0', padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', borderLeft: '3px solid #16a34a', fontSize: '13px', color: '#166534' }}>
                    <i className="fas fa-check-circle" style={{ marginRight: '6px' }}></i>
                    <strong>Báo cáo kết quả ({item.claimed_by_name || 'Nhân viên'}):</strong> {item.completion_note}
                  </div>
                )}

                {/* Media indicators */}
                <div className="card-meta-row">
                  <div className="meta-left">
                    <span className="meta-author">
                      <i className="fas fa-user-circle"></i> {item.author_name}
                    </span>
                    <span className="meta-time">
                      <i className="fas fa-clock"></i> {new Date(item.created_at).toLocaleString('vi-VN')}
                    </span>
                    <span className="meta-target">
                      <i className="fas fa-users"></i>{' '}
                      {item.target_type === 'all'
                        ? 'Toàn thể công ty'
                        : item.target_type === 'department'
                        ? `Bộ phận: ${item.target_department || 'Chưa chọn'}`
                        : `Chỉ định (${item.recipient_ids?.length || 0} người)`}
                    </span>

                    {item.due_date && (
                      <span className="meta-due-date" style={{ color: '#b45309', fontWeight: 600 }}>
                        <i className="fas fa-calendar-check"></i> Hạn: {new Date(item.due_date).toLocaleString('vi-VN')}
                      </span>
                    )}

                    {imageCount > 0 && (
                      <span className="meta-attachment">
                        <i className="fas fa-image"></i> {imageCount} ảnh
                      </span>
                    )}
                    {(videoCount > 0 || item.video_url) && (
                      <span className="meta-attachment">
                        <i className="fas fa-video"></i> {videoCount > 0 ? `${videoCount} video` : 'Video link'}
                      </span>
                    )}
                    {item.require_ack && (
                      <span className="meta-ack" title="Yêu cầu nhân viên bấm xác nhận đã đọc">
                        <i className="fas fa-signature"></i> Cần xác nhận
                      </span>
                    )}
                  </div>

                  {/* Read Rate Meter */}
                  <div className="meta-read-progress" onClick={() => handleOpenTracking(item.id)}>
                    <div className="progress-labels">
                      <span>Đã xem:</span>
                      <strong>
                        {readCount}/{totalRecipients} ({percentRead}%)
                      </strong>
                    </div>
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${percentRead}%`,
                          backgroundColor: percentRead >= 80 ? '#1e7e48' : percentRead >= 40 ? '#b7791f' : '#8d2a2c',
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE / EDIT MODAL */}
      {isModalOpen && (
        <div className="bulletin-modal-backdrop" onClick={() => setIsModalOpen(false)}>
          <div className="bulletin-modal-window" onClick={(e) => e.stopPropagation()}>
            <div className="bulletin-modal-header">
              <h2>
                <i className={editingBulletin ? 'fas fa-edit' : 'fas fa-pen-nib'} style={{ marginRight: '8px' }}></i>
                {editingBulletin ? 'Chỉnh Sửa Thông Tin' : 'Tạo Thông Tin Mới'}
              </h2>
              <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmitForm} className="bulletin-form">
              <div className="bulletin-form-body">
                {/* Category & Priority Row */}
                <div className="form-row-2">
                  <div className="form-group">
                    <label>
                      Loại thông tin <span className="req">*</span>
                    </label>
                    <select
                      value={formCategory}
                      onChange={(e) => setFormCategory(e.target.value as BulletinCategory)}
                      required
                    >
                      <option value="thong_bao">Thông báo</option>
                      <option value="huong_dan">Hướng dẫn / Quy trình SOP</option>
                      <option value="giao_viec">Giao việc / Nhiệm vụ</option>
                      <option value="vi_pham">Vi phạm / Nhắc nhở</option>
                      <option value="khen_thuong">Khen thưởng / Vinh danh</option>
                      <option value="khan_cap">Khẩn cấp</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Mức độ ưu tiên</label>
                    <select
                      value={formPriority}
                      onChange={(e) => setFormPriority(e.target.value as BulletinPriority)}
                    >
                      <option value="normal">Bình thường</option>
                      <option value="important">Quan trọng</option>
                      <option value="urgent">Khẩn cấp</option>
                    </select>
                  </div>
                </div>

                {/* Target Audience Row */}
                <div className="form-group target-selector-group">
                  <label>
                    Đối tượng nhận tin <span className="req">*</span>
                  </label>
                  <div className="target-type-segmented">
                    <button
                      type="button"
                      className={`seg-btn ${formTargetType === 'all' ? 'active' : ''}`}
                      onClick={() => setFormTargetType('all')}
                    >
                      Toàn công ty ({staffList.length})
                    </button>
                    <button
                      type="button"
                      className={`seg-btn ${formTargetType === 'department' ? 'active' : ''}`}
                      onClick={() => setFormTargetType('department')}
                    >
                      Theo bộ phận
                    </button>
                    <button
                      type="button"
                      className={`seg-btn ${formTargetType === 'specific' ? 'active' : ''}`}
                      onClick={() => setFormTargetType('specific')}
                    >
                      Chỉ định cá nhân ({formRecipientIds.length})
                    </button>
                  </div>

                  {/* Department Select Box */}
                  {formTargetType === 'department' && (
                    <div className="department-select-box" style={{ marginTop: '12px', padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#1e293b' }}>
                        <i className="fas fa-users-cog" style={{ marginRight: '6px', color: '#8d2a2c' }}></i>
                        Chọn bộ phận chịu trách nhiệm:
                      </label>
                      <select
                        value={formTargetDepartment}
                        onChange={(e) => setFormTargetDepartment(e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff' }}
                      >
                        {DEPARTMENT_OPTIONS.map((dep) => (
                          <option key={dep.value} value={dep.value}>
                            {dep.label}
                          </option>
                        ))}
                      </select>
                      <small style={{ display: 'block', marginTop: '6px', color: '#64748b' }}>
                        <i className="fas fa-info-circle"></i> Toàn bộ nhân viên thuộc bộ phận này sẽ nhận được nhiệm vụ để phân công và bấm nhận việc.
                      </small>
                    </div>
                  )}

                  {/* Specific Staff Picker */}
                  {formTargetType === 'specific' && (
                    <div className="specific-staff-box">
                      <div className="staff-box-header">
                        <div className="staff-search-input">
                          <i className="fas fa-search"></i>
                          <input
                            type="text"
                            placeholder="Tìm nhân viên theo tên, mã hoặc chức vụ..."
                            value={staffSearchQuery}
                            onChange={(e) => setStaffSearchQuery(e.target.value)}
                          />
                        </div>
                        <div className="staff-box-actions">
                          <button type="button" onClick={handleSelectAllStaff}>
                            Chọn tất cả
                          </button>
                          <button type="button" onClick={handleDeselectAllStaff}>
                            Bỏ chọn
                          </button>
                        </div>
                      </div>

                      <div className="staff-grid-picker">
                        {filteredStaffList.map((st) => {
                          const isSelected = formRecipientIds.includes(st.id);
                          return (
                            <div
                              key={st.id}
                              className={`staff-pick-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleToggleStaffRecipient(st.id)}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}} // handled by parent div
                              />
                              <div className="pick-info">
                                <span className="pick-name">{st.name}</span>
                                <span className="pick-code">
                                  {st.id} {st.role ? `• ${st.role}` : ''}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {formRecipientIds.length === 0 && (
                        <p className="pick-warning">
                          <i className="fas fa-exclamation-triangle"></i> Vui lòng chọn ít nhất một nhân viên.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Task Specific Configuration Row (Only for giao_viec category) */}
                {formCategory === 'giao_viec' && (
                  <div className="task-config-box" style={{ marginBottom: '16px', padding: '14px 16px', background: 'rgba(41, 128, 185, 0.06)', borderRadius: '8px', border: '1px solid rgba(41, 128, 185, 0.25)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: '#1e3a8a', fontWeight: 700 }}>
                      <i className="fas fa-tasks"></i>
                      <span>Thiết lập Giao việc & Phân công nhiệm vụ</span>
                    </div>
                    <div className="form-row-2">
                      <div className="form-group" style={{ marginBottom: '10px' }}>
                        <label>Hạn chót hoàn thành (Deadline):</label>
                        <input
                          type="datetime-local"
                          value={formDueDate}
                          onChange={(e) => setFormDueDate(e.target.value)}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: '10px' }}>
                        <label>Người phụ trách (hoặc để trống để nhân viên tự nhận việc):</label>
                        <select
                          value={formClaimedById}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormClaimedById(val);
                            if (val && formTaskStatus === 'pending') {
                              setFormTaskStatus('in_progress');
                            } else if (!val && formTaskStatus === 'in_progress') {
                              setFormTaskStatus('pending');
                            }
                          }}
                        >
                          <option value="">-- Để trống (Nhân viên trong bộ phận tự nhận) --</option>
                          {staffList.map((st) => (
                            <option key={st.id} value={st.id}>
                              {st.name} ({st.id} {st.role ? `• ${st.role}` : ''})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Trạng thái tiến độ:</label>
                      <select
                        value={formTaskStatus}
                        onChange={(e) => setFormTaskStatus(e.target.value as TaskStatus)}
                      >
                        <option value="pending">Chờ nhận việc</option>
                        <option value="in_progress">Đang thực hiện</option>
                        <option value="completed">Đã hoàn thành</option>
                        <option value="cancelled">Đã hủy</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Title */}
                <div className="form-group">
                  <label>
                    Tiêu đề <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="VD: Thông báo lịch nghỉ lễ hoặc Quy trình mới..."
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    required
                  />
                </div>

                {/* Summary */}
                <div className="form-group">
                  <label>Tóm tắt ngắn (Tùy chọn)</label>
                  <input
                    type="text"
                    placeholder="Tóm tắt ngắn gọn nội dung..."
                    value={formSummary}
                    onChange={(e) => setFormSummary(e.target.value)}
                  />
                </div>

                {/* Detailed Content */}
                <div className="form-group">
                  <label>
                    Nội dung chi tiết <span className="req">*</span>
                  </label>
                  <textarea
                    rows={6}
                    placeholder="Nhập nội dung đầy đủ..."
                    value={formContent}
                    onChange={(e) => setFormContent(e.target.value)}
                    required
                  />
                </div>

                {/* Video URL (Optional) */}
                <div className="form-group">
                  <label>Link Video ngoài (Tùy chọn)</label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={formVideoUrl}
                    onChange={(e) => setFormVideoUrl(e.target.value)}
                  />
                </div>

                {/* Media Attachment Upload (Drag and drop) */}
                <div className="form-group media-upload-group">
                  <label>Hình ảnh & Video đính kèm</label>

                  <div
                    className="media-dropzone"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <i className="fas fa-cloud-upload-alt upload-drop-icon"></i>
                    <p className="drop-main-text">Nhấp để chọn file hoặc kéo thả ảnh/video vào đây</p>
                    <p className="drop-sub-text">Hỗ trợ: JPG, PNG, WEBP, GIF, MP4, WEBM (Tối đa 200MB/file)</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                  </div>

                  {/* Attached and Selected media list */}
                  {(existingMediaFiles.length > 0 || selectedUploadFiles.length > 0) && (
                    <div className="media-preview-strip">
                      {/* Existing Media in Bulletin */}
                      {existingMediaFiles.map((m) => (
                        <div key={m.fileId} className="media-chip-preview">
                          {m.mimeType.startsWith('image/') ? (
                            <img src={m.url} alt={m.originalName} className="thumb-img" />
                          ) : (
                            <div className="thumb-video-placeholder">
                              <i className="fas fa-file-video"></i>
                            </div>
                          )}
                          <span className="thumb-name" title={m.originalName}>
                            {m.originalName}
                          </span>
                          <button
                            type="button"
                            className="btn-remove-thumb"
                            title="Xóa tệp này"
                            onClick={() => handleRemoveExistingMedia(m.fileId)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}

                      {/* Newly selected files */}
                      {selectedUploadFiles.map((file, idx) => (
                        <div key={idx} className="media-chip-preview is-new">
                          <span className="badge-new-upload">Mới</span>
                          <div className="thumb-video-placeholder">
                            <i className={file.type.startsWith('image/') ? 'fas fa-image' : 'fas fa-video'}></i>
                          </div>
                          <span className="thumb-name" title={file.name}>
                            {file.name}
                          </span>
                          <span className="thumb-size">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                          <button
                            type="button"
                            className="btn-remove-thumb"
                            title="Xóa tệp"
                            onClick={() => handleRemoveSelectedFile(idx)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Additional Settings & Toggles */}
                <div className="form-toggles-row">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={formIsPinned}
                      onChange={(e) => setFormIsPinned(e.target.checked)}
                    />
                    <span>
                      <i className="fas fa-thumbtack"></i> Ghim lên đầu bảng tin
                    </span>
                  </label>

                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={formRequireAck}
                      onChange={(e) => setFormRequireAck(e.target.checked)}
                    />
                    <span>
                      <i className="fas fa-check-double"></i> Yêu cầu nhân viên bấm "Xác nhận đã đọc & hiểu"
                    </span>
                  </label>

                  <div className="status-selector-mini">
                    <label>Trạng thái:</label>
                    <select
                      value={formStatus}
                      onChange={(e) => setFormStatus(e.target.value as BulletinStatus)}
                    >
                      <option value="published">Phát hành ngay</option>
                      <option value="draft">Lưu bản nháp</option>
                      <option value="archived">Lưu trữ</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bulletin-modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isSubmitting}
                >
                  Hủy bỏ
                </button>
                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                      Đang xử lý tải lên & lưu trữ...
                    </>
                  ) : editingBulletin ? (
                    'Lưu thay đổi'
                  ) : (
                    'Phát hành thông tin'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TRACKING MODAL */}
      {isTrackingModalOpen && (
        <div className="bulletin-modal-backdrop" onClick={() => setIsTrackingModalOpen(false)}>
          <div className="bulletin-modal-window tracking-window" onClick={(e) => e.stopPropagation()}>
            <div className="bulletin-modal-header">
              <h2>
                <i className="fas fa-chart-pie" style={{ color: '#8d2a2c', marginRight: '8px' }}></i>
                Theo Dõi Tình Trạng Đọc Tin
              </h2>
              <button className="modal-close-btn" onClick={() => setIsTrackingModalOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            {isTrackingLoading ? (
              <div className="bulletin-state-box" style={{ padding: '60px' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: '#8d2a2c' }}></i>
                <p>Đang tải dữ liệu người xem...</p>
              </div>
            ) : trackingData ? (
              <div className="tracking-modal-content">
                <div className="tracking-header-info">
                  <h3 className="track-title">{trackingData.bulletin.title}</h3>
                  <div className="track-summary-bars">
                    <div className="track-stat-item">
                      <span className="stat-label">Tổng đối tượng:</span>
                      <span className="stat-val">{trackingData.summary.total} nhân viên</span>
                    </div>
                    <div className="track-stat-item">
                      <span className="stat-label">Đã đọc:</span>
                      <span className="stat-val text-success">
                        {trackingData.summary.read} ({trackingData.summary.percent_read}%)
                      </span>
                    </div>
                    <div className="track-stat-item">
                      <span className="stat-label">Chưa đọc:</span>
                      <span className="stat-val text-danger">{trackingData.summary.unread}</span>
                    </div>
                    {trackingData.bulletin.require_ack && (
                      <div className="track-stat-item">
                        <span className="stat-label">Đã bấm xác nhận:</span>
                        <span className="stat-val text-primary">{trackingData.summary.acknowledged}</span>
                      </div>
                    )}
                  </div>

                  <div className="track-progress-bar">
                    <div
                      className="bar-fill"
                      style={{ width: `${trackingData.summary.percent_read}%` }}
                    ></div>
                  </div>
                </div>

                {/* Filter and Search inside tracking */}
                <div className="tracking-search-bar">
                  <div className="seg-tracking-filter">
                    <button
                      className={trackingFilter === 'all' ? 'active' : ''}
                      onClick={() => setTrackingFilter('all')}
                    >
                      Tất cả ({trackingData.summary.total})
                    </button>
                    <button
                      className={trackingFilter === 'read' ? 'active' : ''}
                      onClick={() => setTrackingFilter('read')}
                    >
                      Đã đọc ({trackingData.summary.read})
                    </button>
                    <button
                      className={trackingFilter === 'unread' ? 'active' : ''}
                      onClick={() => setTrackingFilter('unread')}
                    >
                      Chưa đọc ({trackingData.summary.unread})
                    </button>
                  </div>

                  <div className="tracking-search-input">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      placeholder="Tìm nhân viên..."
                      value={trackingSearch}
                      onChange={(e) => setTrackingSearch(e.target.value)}
                    />
                  </div>
                </div>

                {/* Recipients Table */}
                <div className="tracking-table-wrapper">
                  <table className="tracking-table">
                    <thead>
                      <tr>
                        <th>Nhân viên</th>
                        <th>Chức vụ</th>
                        <th>Trạng thái đọc</th>
                        <th>Thời gian xem</th>
                        {trackingData.bulletin.require_ack && <th>Xác nhận hiểu</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTrackingRecipients.map((rec) => (
                        <tr key={rec.staff_id}>
                          <td>
                            <div className="staff-cell">
                              <span className="staff-avatar">{rec.staff_name.charAt(0)}</span>
                              <div>
                                <strong>{rec.staff_name}</strong>
                                <span className="staff-sub-code">{rec.staff_id}</span>
                              </div>
                            </div>
                          </td>
                          <td>{rec.role || '—'}</td>
                          <td>
                            {rec.is_read ? (
                              <span className="badge-read-status read">
                                <i className="fas fa-check-circle"></i> Đã đọc
                              </span>
                            ) : (
                              <span className="badge-read-status unread">
                                <i className="fas fa-clock"></i> Chưa đọc
                              </span>
                            )}
                          </td>
                          <td>
                            {rec.read_at ? new Date(rec.read_at).toLocaleString('vi-VN') : '—'}
                          </td>
                          {trackingData.bulletin.require_ack && (
                            <td>
                              {rec.is_acknowledged ? (
                                <span className="badge-ack ack-yes">
                                  <i className="fas fa-check-double"></i> Đã xác nhận
                                </span>
                              ) : (
                                <span className="badge-ack ack-no">
                                  <i className="fas fa-hourglass-half"></i> Chưa xác nhận
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                      {filteredTrackingRecipients.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: '#95a5a6' }}>
                            Không tìm thấy nhân viên nào phù hợp bộ lọc.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            <div className="bulletin-modal-footer">
              <button className="btn-cancel" onClick={() => setIsTrackingModalOpen(false)}>
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PREVIEW MODAL */}
      {previewBulletin && (
        <div className="bulletin-modal-backdrop" onClick={() => setPreviewBulletin(null)}>
          <div className="bulletin-modal-window preview-window" onClick={(e) => e.stopPropagation()}>
            <div className="bulletin-modal-header">
              <h2>
                <i className="fas fa-eye" style={{ color: '#8d2a2c', marginRight: '8px' }}></i>
                Xem Trước Thông Tin (Mô phỏng Nhân viên)
              </h2>
              <button className="modal-close-btn" onClick={() => setPreviewBulletin(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="preview-body">
              <div className="preview-badges">
                <span
                  className="category-badge"
                  style={{
                    color: CATEGORY_MAP[previewBulletin.category]?.color,
                    background: CATEGORY_MAP[previewBulletin.category]?.bg,
                  }}
                >
                  <i className={CATEGORY_MAP[previewBulletin.category]?.icon}></i>{' '}
                  {CATEGORY_MAP[previewBulletin.category]?.label}
                </span>

                {previewBulletin.priority !== 'normal' && (
                  <span
                    className="priority-badge"
                    style={{ color: PRIORITY_MAP[previewBulletin.priority]?.color }}
                  >
                    {PRIORITY_MAP[previewBulletin.priority]?.label}
                  </span>
                )}

                {previewBulletin.category === 'giao_viec' && previewBulletin.task_status && (
                  <span className={`task-status-badge status-${previewBulletin.task_status}`}>
                    <i className={TASK_STATUS_MAP[previewBulletin.task_status]?.icon || 'fas fa-tasks'}></i>{' '}
                    {TASK_STATUS_MAP[previewBulletin.task_status]?.label}
                    {previewBulletin.claimed_by_name ? ` (${previewBulletin.claimed_by_name})` : ''}
                  </span>
                )}

                {previewBulletin.target_type === 'department' && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '4px', background: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: '12px' }}>
                    <i className="fas fa-building"></i> Bộ phận: {previewBulletin.target_department}
                  </span>
                )}
              </div>

              <h1 className="preview-title">{previewBulletin.title}</h1>
              <div className="preview-meta">
                <span>
                  <i className="fas fa-user-edit"></i> Đăng bởi: {previewBulletin.author_name}
                </span>
                <span>
                  <i className="fas fa-calendar-alt"></i> Ngày đăng:{' '}
                  {new Date(previewBulletin.created_at).toLocaleString('vi-VN')}
                </span>
                {previewBulletin.due_date && (
                  <span style={{ color: '#b45309', fontWeight: 600 }}>
                    <i className="fas fa-calendar-check"></i> Hạn:{' '}
                    {new Date(previewBulletin.due_date).toLocaleString('vi-VN')}
                  </span>
                )}
              </div>

              {previewBulletin.completion_note && (
                <div style={{ margin: '14px 0', padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px', borderLeft: '4px solid #16a34a', color: '#166534' }}>
                  <strong><i className="fas fa-check-circle"></i> Báo cáo hoàn thành:</strong> {previewBulletin.completion_note}
                </div>
              )}

              {previewBulletin.summary && (
                <div className="preview-callout">{previewBulletin.summary}</div>
              )}

              <div className="preview-content" style={{ whiteSpace: 'pre-wrap' }}>
                {previewBulletin.content}
              </div>

              {/* Video Embed */}
              {previewBulletin.video_url && (
                <div className="preview-video-container">
                  <h4>
                    <i className="fas fa-video"></i> Video đính kèm:
                  </h4>
                  {previewBulletin.video_url.includes('youtube.com') ||
                  previewBulletin.video_url.includes('youtu.be') ? (
                    <iframe
                      className="video-iframe"
                      src={
                        previewBulletin.video_url.includes('watch?v=')
                          ? previewBulletin.video_url.replace('watch?v=', 'embed/')
                          : previewBulletin.video_url.replace('youtu.be/', 'www.youtube.com/embed/')
                      }
                      title="YouTube video"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <video controls className="video-player">
                      <source src={previewBulletin.video_url} />
                      Trình duyệt không hỗ trợ phát video.
                    </video>
                  )}
                </div>
              )}

              {/* Uploaded Media Gallery */}
              {previewBulletin.media_files && previewBulletin.media_files.length > 0 && (
                <div className="preview-media-section">
                  <h4>
                    <i className="fas fa-images"></i> Tệp đính kèm ({previewBulletin.media_files.length}):
                  </h4>
                  <div className="preview-media-grid">
                    {previewBulletin.media_files.map((m) => (
                      <div key={m.fileId} className="preview-media-card">
                        {m.mimeType.startsWith('image/') ? (
                          <a href={m.url} target="_blank" rel="noreferrer">
                            <img src={m.url} alt={m.originalName} className="preview-grid-img" />
                          </a>
                        ) : (
                          <video controls className="preview-grid-video">
                            <source src={m.url} type={m.mimeType} />
                          </video>
                        )}
                        <span className="media-card-title">{m.originalName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bulletin-modal-footer">
              <button className="btn-cancel" onClick={() => setPreviewBulletin(null)}>
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default AdminBulletinPage;
